<template>
  <div class="home-page">
    <Header />
    
    <main class="main-content">
      <div class="overlay"></div>
      <!-- Hero Section -->
      <section class="hero-section">
        <div class="slider-container">
          <div class="slider">
            <div v-for="(slide, index) in slides" :key="index" class="slide" :class="{ active: currentSlide === index }">
              <router-link :to="slide.link">
                <img :src="require(`@/assets/images/${slide.image}`)" :alt="slide.alt">
              </router-link>
            </div>
          </div>
          <div class="slider-nav">
            <button class="nav-button prev-button" @click="prevSlide">
              <i class="fas fa-chevron-left"></i>
            </button>
            <button class="nav-button next-button" @click="nextSlide">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
          <div class="slider-dots">
            <button v-for="(slide, index) in slides" :key="index" 
                    class="dot" :class="{ active: currentSlide === index }" 
                    @click="setSlide(index)" 
                    :aria-label="'转到第' + (index + 1) + '张幻灯片'"></button>
          </div>
        </div>
      </section>

      <!-- Pain Points Section -->
      <section class="pain-points" id="pain-points">
        <div class="pain-points-container">
          <h2 class="pain-points-title" data-aos="fade-up">您的企业是否正在面临以下困扰？</h2>
          <div class="pain-points-content">
            <div class="pain-points-left" data-aos="fade-right" data-aos-delay="200">
              <div v-for="(point, index) in painPointsLeft" :key="'left-' + index" class="pain-point-item" :data-aos="'fade-up'" :data-aos-delay="300 + index * 150">
                <i :class="point.icon + ' pain-point-icon'"></i>
                <div class="pain-point-text">
                  <h3 class="pain-point-title">{{ point.title }}</h3>
                  <p class="pain-point-desc">{{ point.description }}</p>
                </div>
              </div>
            </div>

            <div class="pain-points-center" data-aos="zoom-in" data-aos-delay="400">
              <img src="@/assets/images/kunrao.png" alt="企业困扰" class="center-image">
            </div>

            <div class="pain-points-right" data-aos="fade-left" data-aos-delay="200">
              <div v-for="(point, index) in painPointsRight" :key="'right-' + index" class="pain-point-item" :data-aos="'fade-up'" :data-aos-delay="600 + index * 150">
                <i :class="point.icon + ' pain-point-icon'"></i>
                <div class="pain-point-text">
                  <h3 class="pain-point-title">{{ point.title }}</h3>
                  <p class="pain-point-desc">{{ point.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Design Center Section -->
      <section class="design-center">
        <div class="container">
          <h2 class="section-title" data-aos="fade-up">一站式设计中心，多人员精准设计</h2>
          <div class="design-content">
            <div class="design-tabs" data-aos="fade-up" data-aos-delay="200">
              <button v-for="(tab, index) in designTabs" 
                      :key="index" 
                      class="design-tab" 
                      :class="{ active: activeDesignTab === index }"
                      @click="setActiveDesignTab(index)">
                {{ tab.title }}
              </button>
            </div>

            <div class="design-panes">
              <div v-for="(tab, index) in designTabs" 
                   :key="index" 
                   class="design-pane" 
                   :class="{ active: activeDesignTab === index }">
                <div class="design-grid">
                  <div class="design-image">
                    <img :src="require(`@/assets/images/${tab.image}`)" :alt="tab.title">
                  </div>
                  <div class="design-info">
                    <h3>{{ tab.subtitle }}</h3>
                    <p>{{ tab.description }}</p>
                    <div class="feature-list">
                      <div v-for="(feature, fIndex) in tab.features" :key="fIndex" class="feature-item">
                        <span class="feature-icon"></span>
                        <span class="feature-text">{{ feature }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Advantages Section -->
      <section class="advantages" id="advantages">
        <div class="container">
          <h2 class="section-title" data-aos="fade-up">我们的优势</h2>
          <div class="advantages-scroll-container" data-aos="fade-up" data-aos-delay="200">
            <div class="advantages-scroll-track" ref="advantagesTrack">
              <div v-for="(advantage, index) in [...advantages, ...advantages]" :key="`${index}-${index < advantages.length ? 'original' : 'clone'}`" class="advantage-item">
                <img :src="require(`@/assets/images/${advantage.image}`)" :alt="advantage.title">
                <div class="advantage-content">
                  <h3>{{ advantage.title }}</h3>
                  <p>{{ advantage.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Scene Section -->
      <section class="scene-section">
        <div class="container">
          <h2 class="scene-title" data-aos="fade-up">多个环境下，随时随地管理车间现场</h2>
          <div class="scene-grid">
            <div v-for="(scene, index) in scenes" :key="index" class="scene-card" :data-color="scene.color" :data-aos="'fade-up'" :data-aos-delay="200 + index * 150">
              <div class="scene-image">
                <img :src="require(`@/assets/images/${scene.image}`)" :alt="scene.title">
              </div>
              <div class="scene-content">
                <h3>{{ scene.title }}</h3>
                <p>{{ scene.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Testimonials Section -->
      <section class="testimonials-section">
        <div class="container">
          <h2 class="testimonials-title">客户证言</h2>
          
          <div class="industry-tabs">
            <button v-for="(industry, index) in industries" 
                    :key="index" 
                    class="industry-tab" 
                    :class="{ active: activeIndustryTab === index }"
                    @click="setActiveIndustryTab(index)"
                    :data-industry="industry.key">
              {{ industry.name }}
            </button>
          </div>

          <div class="testimonials-container">
            <div v-for="(industry, industryIndex) in industries" 
                 :key="industryIndex" 
                 class="testimonials-group" 
                 :class="{ active: activeIndustryTab === industryIndex }" 
                 :data-industry="industry.key">
              <div v-for="(testimonial, index) in industry.testimonials" 
                   :key="index" 
                   class="testimonial-card" 
                   :data-gradient="testimonial.gradient">
                <div class="testimonial-header">
                  <img :src="require(`@/assets/images/${testimonial.avatar}`)" :alt="testimonial.name" class="client-avatar">
                  <h3>{{ testimonial.name }}</h3>
                </div>
                <div class="testimonial-content">
                  <span class="quote-mark">"</span>
                  {{ testimonial.content }}
                  <span class="quote-mark">"</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Partners Section -->
      <section class="partners" id="partners">
        <div class="container">
          <h2 class="section-title">众多合作客户见证我们成长</h2>
          <div class="partners-grid">
            <div v-for="(partner, index) in partners" :key="index" class="partner-item">
              <img :src="require(`@/assets/images/${partner.image}`)" :alt="partner.name">
              <div class="partner-overlay">
                <h3>{{ partner.name }}</h3>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    
    <!-- 移除了多余的扫码关注部分 -->
    
    <Footer />
    <SideService />
  </div>
</template>

<script>
import Header from '@/components/Header.vue';
import Footer from '@/components/Footer.vue';
import SideService from '@/components/SideService.vue';

export default {
  name: 'Home',
  components: {
    Header,
    Footer,
    SideService
  },
  provide() {
    return {
      showSideServicePopup: this.showSideServicePopup,
      hideSideServicePopup: this.hideSideServicePopup,
      scrollToTop: this.scrollToTop
    };
  },
  data() {
    return {
      currentSlide: 0,
      activeDesignTab: 0,
      activeIndustryTab: 0,
      slideInterval: null,
      slides: [
        { image: 'slide1.jpg', alt: '一体化 智能ERP企业管理平台', link: '/pro' },
        { image: 'slide2.jpg', alt: '可视化数据看板', link: '/function' },
        { image: 'slide3.jpg', alt: '腾智.管简单', link: 'https://sdtz.guanjd.com/#/' }
      ],
      painPointsLeft: [
        { icon: 'fas fa-truck', title: '供应链效率低', description: '供应商数量多、分布广，零部件规格多、采购周期长、价格不透明、需求波动大等，从计划到交付全链路不可视，严重影响生产和交付' },
        { icon: 'fas fa-warehouse', title: '库存不同步', description: '仓库类型多、地点多、库存多、变动快，各类型库存无法联动，不能快速处理呆滞、冻结料等，库存成本居高不下，决策和管理难' },
        { icon: 'fas fa-industry', title: '生产不透明', description: '生产现场产线、设备、工序、批次等较多，各个环节数据独立，难以协同和一致管理，无法快速发现、定位、解决问题，损失和风险大' }
      ],
      painPointsRight: [
        { icon: 'fas fa-project-diagram', title: '产品协同难', description: '产品版本多、更新快、数据多，产研、设计、BOM、工艺、车间、设备、模具等数据分散，不同步，易出错，管理和追溯难' },
        { icon: 'fas fa-check-circle', title: '质量管理难', description: '质量数据分散在不同供应商、原材料、产线、质检、部门、人员等环节，无法全面整合，缺乏高效的质量管理和追溯手段' },
        { icon: 'fas fa-money-bill-wave', title: '成本控制难', description: '原材料、制造、设备、人员等各种费用，无法精细化记录和管理，缺少多维度、专业化的成本归集、核算、分析方法，无法及时预警或优化' }
      ],
      designTabs: [
        {
          title: '供应商准交率',
          image: 'gongyingshang.jpg',
          subtitle: '交货总数、准交次数，实时统计分析',
          description: '供应商供货能力科学评估，保障供应链安全稳定',
          features: [
            '提供供应商准时交货统计表，自动分析采购预计到货与收货单/入库申请单到货时间',
            '采购交货情况延期、正常还是提前以及对应天数实时显示，不同颜色区分，一目了然',
            '支持按采购时间，从年、月、周、日方式维度对比分析供应商交货率',
            '支持按供应商、按产品、按排序等自定义检索条件，多维度对比分析供应商交货率',
            '支持柱状图、折线图等图形化显示分析结果，直观易懂，满足更多场景、角色需求'
          ]
        },
        {
          title: '智能物料管理',
          image: 'znwlgl.jpg',
          subtitle: '一键物料分析，结果直达采购流程',
          description: '自定义物料分析策略，结果自动生成，一键提交预购单',
          features: [
            '自动分析需求量与库存、最大程度利用现有库存、一键预购，精准采购',
            '可通过【订单物料采购追踪表】清晰查看缺料的补货实时进度',
            '支持批量导入物料清单，变更审批，变更过程详细记录，BOM支持无限层级',
            '非标物料清单一键导入、bom子件自动生成，支持选择多个适用物料清单',
            '可根据业务需求，灵活设置物料领、补、退、废审批流程',
            '一键设置"按单领料"等策略，节约成本，有效管控'
          ]
        },
        {
          title: '生产订单追踪',
          image: 'scddgz.jpg',
          subtitle: '实时掌控生产订单进度',
          description: '在一个界面里集中追踪查看所有订单进度',
          features: [
            '一键追踪生产订单的当前进度，包括产量进度和工期进度',
            '直观显示订单剩余或延误时间，便于调整纠正',
            '完整显示派工核定量、完成量、合格量、不合格量等关键数据',
            '可实现由销售订单到生产订单的一键穿透追踪'
          ]
        },
        {
          title: '智能扫码报工',
          image: 'znsm.jpg',
          subtitle: '支持手机APP扫码报工',
          description: '无障碍链接智能终端，支持PDA、APP等高效报工方式',
          features: [
            '灵活设置不同产品的周转码、可实现一物一码、一单一码、一箱一码',
            '手机APP扫码报工，轻松解决不同场地应用问题',
            '报工结果直接关联计件工资，过程省时高效',
            '可支持班组长代报工、灵活适应多种生产流程'
          ]
        },
        {
          title: '产品品质管控',
          image: 'cpjk.jpg',
          subtitle: '提供生产质检和委外质检',
          description: '自定义设置质检字段、编号、类型、等级，支持质检单跟进',
          features: [
            '合格品入库、不合格品返修，降低废品率，为企业节约成本',
            '为每个产品建立唯一标记，支持抽检和全检',
            '支持每道工序和产品质检，严控产品质量，减少退货、索赔风险',
            '帮您追踪产品问题根源，及时改进'
          ]
        },
        {
          title: '智能成本核算',
          image: 'zncbhs.jpg',
          subtitle: '生产成本自动核算，过程简练、结果精确',
          description: '解决生产环节参与运算的数据多、核算过程耗时长等问题',
          features: [
            '支持一键查询费用分摊细则（订单间费用、订单内费用）',
            '支持一键设置不同费用分摊标准、多种核算方式（品种法、分步法、订单法）',
            '支持按在产成本、委外成本、费用成本、工资成本等多维度查看成本核算报表',
            '支持一键导出《产品成本汇总表》',
            '系统对参与核算数据正确性进行实时检测，错误提醒'
          ]
        }
      ],
      advantages: [],
      scenes: [],
      industries: [],
      partners: [],
      qrcodes: [
        { image: 'gongzhonghao.jpg', alt: '公众号二维码', title: '公众号' },
        { image: 'shipinhao.jpg', alt: '视频号二维码', title: '视频号' },
        { image: 'douyin.jpg', alt: '抖音二维码', title: '抖音' },
        { image: 'xiaohongshu.jpg', alt: '小红书二维码', title: '小红书' }
      ]
    };
  },
  methods: {
    prevSlide() {
      this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    },
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    },
    setSlide(index) {
      this.currentSlide = index;
      // 重置自动轮播计时器
      this.resetSlideAutoplay();
    },
    setActiveDesignTab(index) {
      this.activeDesignTab = index;
    },
    setActiveIndustryTab(index) {
      this.activeIndustryTab = index;
    },
    startSlideAutoplay() {
      // 每5秒自动切换一次幻灯片
      this.slideInterval = setInterval(() => {
        this.nextSlide();
      }, 5000);
    },
    resetSlideAutoplay() {
      clearInterval(this.slideInterval);
      this.startSlideAutoplay();
    },
    startAdvantagesScroll() {
      // CSS已处理无缝滚动，无需JavaScript控制
      return;
    },
    initAdvantages() {
      this.advantages = [
        {
          title: '专业的研发团队',
          image: 'zyyftd.jpg',
          description: '十年研发经验，持续技术创新'
        },
        {
          title: '丰富的行业经验',
          image: 'ffhyjy.jpg',
          description: '服务超过1000家企业'
        },
        {
          title: '高效的实施团队',
          image: 'gxsgtd.jpg',
          description: '快速部署，专业培训'
        },
        {
          title: '便捷的操作体验',
          image: 'bjczty.jpg',
          description: '简单易用，快速上手'
        },
        {
          title: '完整的培训体系',
          image: 'wzpxtx.jpg',
          description: '系统培训，持续指导'
        },
        {
          title: '可靠的数据安全',
          image: 'kksjaq.jpg',
          description: '多重备份，安全保障'
        }
      ];
    },
    initScenes() {
      this.scenes = [
        {
          title: '管理层',
          image: 'guanlicheng.jpg',
          description: '随时随地用手机监控关键指标',
          color: 'blue'
        },
        {
          title: '业务主管',
          image: 'yewuzhuguan.jpg',
          description: '周期性的数据统计、分析、优化',
          color: 'green'
        },
        {
          title: '车间现场',
          image: 'chejianxianchang.jpg',
          description: '查看关键数据，快速优化作业瓶颈',
          color: 'purple'
        },
        {
          title: '一站式运营驾驶舱',
          image: 'yzsyyjsc.jpg',
          description: '随时随地监控生产进度',
          color: 'orange'
        }
      ];
    },
    showSideServicePopup(index) {
      const popups = document.querySelectorAll('.service-popup');
      // 先隐藏所有弹出层
      popups.forEach(popup => {
        popup.style.display = 'none';
      });
      
      // 显示当前点击的服务项对应的弹出层
      if (popups[index]) {
        popups[index].style.display = 'block';
      }
    },
    hideSideServicePopup() {
      const popups = document.querySelectorAll('.service-popup');
      popups.forEach(popup => {
        popup.style.display = 'none';
      });
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },
    initIndustries() {
      this.industries = [
        {
          key: 'machining',
          name: '机加工行业',
          testimonials: [
            {
              name: '机加工行业总监',
              avatar: 'a1.jpg',
              content: '系统帮助我们提升了30%的生产效率，实现了精细化管理',
              gradient: 'blue'
            },
            {
              name: '生产主管',
              avatar: 'a2.jpg',
              content: '数据可视化功能让生产监控变得如此简单',
              gradient: 'green'
            },
            {
              name: '车间主任',
              avatar: 'a3.jpg',
              content: '工单管理系统极大提高了我们的工作效率',
              gradient: 'purple'
            },
            {
              name: '采购经理',
              avatar: 'a4.jpg',
              content: '库存预警和采购建议功能帮助我们优化了采购流程',
              gradient: 'orange'
            }
          ]
        },
        {
          key: 'mold',
          name: '模具行业',
          testimonials: [
            {
              name: '模具厂厂长',
              avatar: 'b1.jpg',
              content: '库存管理功能帮助我们降低了库存成本',
              gradient: 'orange'
            },
            {
              name: '技术总监',
              avatar: 'b2.jpg',
              content: '生产计划排程更加合理，交付周期缩短了20%',
              gradient: 'green'
            },
            {
              name: '生产经理',
              avatar: 'b3.jpg',
              content: '质量追溯系统帮助我们提升了产品合格率',
              gradient: 'blue'
            },
            {
              name: '设备主管',
              avatar: 'b4.jpg',
              content: '设备维护提醒功能让我们的设备管理更加规范',
              gradient: 'purple'
            }
          ]
        },
        {
          key: 'printing',
          name: '印刷行业',
          testimonials: [
            {
              name: '印刷厂负责人',
              avatar: 'c1.jpg',
              content: '智能排产系统大大提升了我们的生产效率',
              gradient: 'purple'
            },
            {
              name: '运营总监',
              avatar: 'c2.jpg',
              content: '成本核算更加精准，利润分析一目了然',
              gradient: 'orange'
            },
            {
              name: '生产主管',
              avatar: 'c3.jpg',
              content: '物料管理系统帮助我们降低了库存积压',
              gradient: 'blue'
            },
            {
              name: '客户经理',
              avatar: 'c4.jpg',
              content: '订单跟踪系统让客户服务更加专业高效',
              gradient: 'green'
            }
          ]
        },
        {
          key: 'fastener',
          name: '紧固件行业',
          testimonials: [
            {
              name: '企业负责人',
              avatar: 'd1.jpg',
              content: '生产数据实时监控，管理决策更加准确',
              gradient: 'green'
            },
            {
              name: '品质经理',
              avatar: 'd2.jpg',
              content: '质量管理体系更加完善，客户满意度提升',
              gradient: 'purple'
            },
            {
              name: '生产主管',
              avatar: 'd3.jpg',
              content: '生产进度一目了然，管理效率显著提升',
              gradient: 'orange'
            },
            {
              name: '采购总监',
              avatar: 'd4.jpg',
              content: '采购流程更加规范，成本控制更加精准',
              gradient: 'blue'
            }
          ]
        },
        {
          key: 'auto',
          name: '汽配行业',
          testimonials: [
            {
              name: '汽配企业总经理',
              avatar: 'e1.jpg',
              content: '系统帮助我们建立了完整的追溯体系',
              gradient: 'blue'
            },
            {
              name: '技术主管',
              avatar: 'e2.jpg',
              content: '产品质量管控更加严格，废品率大幅降低',
              gradient: 'green'
            },
            {
              name: '生产总监',
              avatar: 'e3.jpg',
              content: '生产效率提升显著，员工管理更加规范',
              gradient: 'purple'
            },
            {
              name: '供应链总监',
              avatar: 'e4.jpg',
              content: '供应商协同功能让采购管理更加高效',
              gradient: 'orange'
            }
          ]
        },
        {
          key: 'pneumatic',
          name: '气动行业',
          testimonials: [
            {
              name: '气动设备厂长',
              avatar: 'f1.jpg',
              content: '生产计划执行更加灵活，产能利用率提升',
              gradient: 'orange'
            },
            {
              name: '运营经理',
              avatar: 'f2.jpg',
              content: '数据分析功能强大，决策更加科学',
              gradient: 'blue'
            },
            {
              name: '质量主管',
              avatar: 'f3.jpg',
              content: '品质追溯更加便捷，客户投诉大幅减少',
              gradient: 'green'
            },
            {
              name: '研发主管',
              avatar: 'f4.jpg',
              content: '产品研发流程更加规范，创新效率提升',
              gradient: 'purple'
            }
          ]
        }
      ];
    },
    initPartners() {
      this.partners = [
        { name: '江苏星火集团', image: 'jsxhjt.png' },
        { name: '奥克斯', image: 'auxs.png' },
        { name: '腾达科技', image: 'tdkj.png' },
        { name: '贝发集团', image: 'beifa.png' },
        { name: '法尔胜泓异集团', image: 'aeshsjt.png' },
        { name: '丰州科技', image: 'fzkj.png' },
        { name: '海天集团', image: 'haitian.png' },
        { name: '海燕玻璃', image: 'haiyan.png' },
        { name: '华为', image: 'huawei.png' },
        { name: '华飞科技', image: 'huafei.png' },
        { name: '金桥焊材', image: 'jqhc.png' },
        { name: '聚思自动化', image: 'jusi.png' },
        { name: '科瑞五金', image: 'krwj.png' },
        { name: '腾龙精细集团', image: 'tenglong.png' },
        { name: '宁波科源石化公司', image: 'keyuan.png' },
        { name: '前程石化', image: 'qiancheng.png' },
        { name: '山东威达集团', image: 'weida.png' },
        { name: '辛化集团', image: 'xinhua.png' },
        { name: '雪龍集团', image: 'xuelong.png' },
        { name: '亚太新材料', image: 'yatai.png' },
        { name: '舟山正源', image: 'zhengyuan.png' },
        { name: '贝因美', image: 'beiyinmei.png' },
        { name: '国家电网', image: 'gjdw.png' },
        { name: '有色金属研究院', image: 'ysjsyjy.png' }
      ];
    }
  },
  mounted() {
    // 初始化动画效果
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 1000,
        once: true,
        offset: 100
      });
    }

    // 初始化数据
    this.initAdvantages();
    this.initScenes();
    this.initIndustries();
    this.initPartners();

    // 自动轮播
    this.startSlideAutoplay();
    
    // 启动优势板块滚动
    this.$nextTick(() => {
      this.startAdvantagesScroll();
    });
  },
  beforeDestroy() {
    // 清除定时器
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }
  }
}
</script>

<style scoped>
/* 全局样式 */
.home-page {
  position: relative;
  overflow-x: hidden;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: none;
}

.overlay.active {
  display: block;
}

.container {
  max-width: 1450px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-title {
  font-size: 2.2rem;
  text-align: center;
  margin-bottom: 2.5rem;
  color: #333;
  font-weight: 600;
  position: relative;
}

.section-title:after {
  content: '';
  position: absolute;
  width: 80px;
  height: 3px;
  background: #009b7b;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
}

/* 英雄部分 */
.hero-section {
  position: relative;
  width: 100%;
  height: calc(100vh - 80px);
  overflow: hidden;
  margin-bottom: 0;
}

.slider-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.slider {
  position: relative;
  width: 100%;
  height: 100%;
}

.slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 1s ease-in-out;
}

.slide.active {
  opacity: 1;
}

.slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.5s ease;
}

.slide:hover img {
  transform: scale(1.05);
}

.slider-nav {
  position: absolute;
  width: 100%;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  display: flex;
  justify-content: space-between;
  padding: 0 2rem;
}

.nav-button {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
  border: none;
  color: #333;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-button:hover {
  background: rgba(255, 255, 255, 0.9);
}

.slider-dots {
  position: absolute;
  bottom: 20px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 10px;
  z-index: 10;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  border: none;
  cursor: pointer;
  transition: all 0.3s;
}

.dot.active {
  background: #fff;
  transform: scale(1.2);
}

/* 痛点部分 */
.pain-points {
  padding: 5rem 0;
  background-color: #f8f9fa;
}

.pain-points-container {
  max-width: 1450px;
  margin: 0 auto;
  padding: 0 20px;
}

.pain-points-title {
  font-size: 2.2rem;
  text-align: center;
  margin-bottom: 3rem;
  color: #333;
  font-weight: 600;
  position: relative;
}

.pain-points-title:after {
  content: '';
  position: absolute;
  width: 80px;
  height: 3px;
  background: #009b7b;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
}

.pain-points-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.pain-points-left,
.pain-points-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.pain-points-center {
  flex: 0 0 300px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.center-image {
  max-width: 100%;
  height: auto;
  border-radius: 50%;
  border: 2px solid red;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.5s;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
}

.center-image:hover {
  transform: scale(1.05);
}

.pain-point-item {
  display: flex;
  align-items: flex-start;
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
}

.pain-point-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.pain-point-icon {
  font-size: 2rem;
  color: #009b7b;
  margin-right: 20px;
  margin-top: 5px;
}

.pain-point-title {
  font-size: 1.2rem;
  margin-bottom: 10px;
  color: #333;
  font-weight: 600;
}

.pain-point-desc {
  color: #666;
  font-size: 0.95rem;
  line-height: 1.6;
}

/* 设计中心部分 */
.design-center {
  padding: 5rem 0;
  background: white;
}

.design-content {
  margin-top: 2rem;
}

.design-tabs {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 30px;
}

.design-tab {
  padding: 12px 25px;
  background: #f0f5ff;
  border: none;
  border-radius: 30px;
  color: #333;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.design-tab.active {
  background: #009b7b;
  color: white;
}

.design-panes {
  position: relative;
  min-height: 400px;
}

.design-pane {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  opacity: 0;
  visibility: hidden;
  transition: all 0.5s;
}

.design-pane.active {
  opacity: 1;
  visibility: visible;
}

.design-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
}

.design-image img {
  max-width: 100%;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.design-info h3 {
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #333;
}

.design-info p {
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.feature-item {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  color: #444;
}

.feature-icon {
  width: 20px;
  height: 20px;
  background: #009b7b;
  border-radius: 50%;
  margin-right: 10px;
  position: relative;
}

.feature-icon:before {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
}

/* 优势部分 */
.advantages {
  padding: 60px 0;
  background: linear-gradient(135deg, #f8faf9 0%, #e5f4f1 100%);
  overflow: hidden;
}

.advantages-scroll-container {
  overflow: hidden;
  width: 100%;
  position: relative;
}

.advantages-scroll-track {
  display: flex;
  gap: 30px;
  animation: scrollAdvantages 30s linear infinite;
  will-change: transform;
}

.advantages-scroll-track:hover {
  animation-play-state: paused;
}

@keyframes scrollAdvantages {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(-50% - 15px));
  }
}

.advantage-item {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  flex: 0 0 280px;
  min-width: 280px;
}

.advantage-item:hover {
  transform: translateY(-10px);
}

.advantage-item img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.advantage-content {
  padding: 20px;
}

.advantage-content h3 {
  font-size: 1.3rem;
  margin-bottom: 10px;
  color: #333;
}

.advantage-content p {
  color: #666;
  font-size: 0.95rem;
  line-height: 1.6;
}


/* 场景部分 */
.scene-section {
  padding: 5rem 0;
  background: white;
}

.scene-title {
  font-size: 2.2rem;
  text-align: center;
  margin-bottom: 3rem;
  color: #333;
  font-weight: 600;
  position: relative;
}

.scene-title:after {
  content: '';
  position: absolute;
  width: 80px;
  height: 3px;
  background: #009b7b;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
}

.scene-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.scene-card {
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.scene-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.scene-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: transparent;
  transition: all 0.3s ease;
}

.scene-card:hover::after {
  background: currentColor;
  opacity: 0.1;
}

.scene-image img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.scene-content {
  padding: 20px;
}

.scene-content h3 {
  font-size: 1.3rem;
  margin-bottom: 10px;
  font-weight: 600;
}

.scene-content p {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #666;
}

.scene-card[data-color="blue"] {
  color: #009b7b;
}

.scene-card[data-color="green"] {
  color: #10b981;
}

.scene-card[data-color="orange"] {
  color: #f97316;
}

.scene-card[data-color="purple"] {
  color: #8b5cf6;
}

/* 客户证言部分 */
.testimonials-section {
  padding: 5rem 0;
  background: #f8f9fa;
  position: relative;
  overflow: hidden;
}

.testimonials-title {
  font-size: 2.2rem;
  text-align: center;
  margin-bottom: 3rem;
  color: #333;
  font-weight: 600;
  position: relative;
}

.testimonials-title:after {
  content: '';
  position: absolute;
  width: 80px;
  height: 3px;
  background: #009b7b;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
}

.testimonials-section:before,
.testimonials-section:after {
  content: '';
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(0, 120, 212, 0.05);
  z-index: 0;
}

.testimonials-section:before {
  top: -100px;
  left: -100px;
}

.testimonials-section:after {
  bottom: -100px;
  right: -100px;
}

.industry-tabs {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
}

.industry-tab {
  padding: 12px 25px;
  background: #f0f5ff;
  border: none;
  border-radius: 30px;
  color: #333;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.industry-tab.active {
  background: #009b7b;
  color: white;
}

.testimonials-group {
  display: none;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding: 0 15px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.testimonials-group.active {
  display: grid;
  opacity: 1;
}

.testimonials-container {
  margin-top: 30px;
  width: 100%;
}

.testimonial-card {
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
}

.testimonial-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.client-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 15px;
}

.testimonial-header h3 {
  font-size: 1.1rem;
  color: #333;
  margin: 0;
}

.testimonial-content {
  font-size: 1rem;
  color: #555;
  line-height: 1.6;
  position: relative;
  padding: 0 10px;
}

.quote-mark {
  font-size: 1.5rem;
  color: #009b7b;
  font-weight: bold;
}

.testimonial-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.testimonial-card[data-gradient="blue"] {
  background: linear-gradient(135deg, #ffffff 0%, #e5f4f1 100%);
}

.testimonial-card[data-gradient="green"] {
  background: linear-gradient(135deg, #ffffff 0%, #e8f5f2 100%);
}

.testimonial-card[data-gradient="orange"] {
  background: linear-gradient(135deg, #ffffff 0%, #e9f5f2 100%);
}

.testimonial-card[data-gradient="purple"] {
  background: linear-gradient(135deg, #ffffff 0%, #f0f7f5 100%);
}

.testimonial-card[data-gradient="red"] {
  background: linear-gradient(135deg, #ffffff 0%, #fef2f2 100%);
}

/* 合作伙伴部分 */
.partners {
  padding: 5rem 0;
  background: white;
}

.partners-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 20px;
  margin: 0 auto;
  padding: 0 15px;
}

@media (max-width: 1200px) {
  .partners-grid {
    grid-template-columns: repeat(6, 1fr);
  }
}

@media (max-width: 992px) {
  .partners-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 768px) {
  .partners-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 576px) {
  .partners-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.partner-item {
  position: relative;
  background: white;
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  aspect-ratio: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.partner-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.partner-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transition: opacity 0.3s;
}

.partner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 155, 123, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.partner-item:hover .partner-overlay {
  opacity: 1;
}

.partner-overlay h3 {
  color: white;
  font-size: 1.1rem;
  text-align: center;
  padding: 0 15px;
  font-weight: 500;
}

@media (max-width: 1200px) {
  .partners-grid {
    grid-template-columns: repeat(6, 1fr);
  }
}

@media (max-width: 992px) {
  .partners-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 768px) {
  .partners-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 576px) {
  .partners-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* 客户证言响应式布局 */
@media (max-width: 1200px) {
  .testimonials-group {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .partners-grid {
    grid-template-columns: repeat(6, 1fr);
  }
}

@media (max-width: 992px) {
  .testimonials-group {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .partners-grid {
    grid-template-columns: repeat(4, 1fr);
  }
  
  .slider-track {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .design-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .design-image {
    order: 2;
  }
  
  .feature-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .hero-section {
    height: 400px;
  }
  
  .pain-points-content {
    flex-direction: column;
  }
  
  .pain-points-center {
    order: -1;
    margin-bottom: 30px;
  }
  
  .scene-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .industry-tabs {
    flex-wrap: wrap;
    gap: 10px;
  }
  
  .industry-tab {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
  
  .testimonials-title {
    font-size: 2rem;
  }
  
  .partners-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 576px) {
  .section-title {
    font-size: 1.8rem;
  }
  
  .slider-track {
    grid-template-columns: 1fr;
  }
  
  .scene-grid {
    grid-template-columns: 1fr;
  }
  
  .design-info h3 {
    font-size: 1.5rem;
  }
  
  .design-info p {
    font-size: 1rem;
  }
  
  .testimonials-group {
    grid-template-columns: 1fr;
  }
  
  .testimonial-card {
    padding: 20px;
  }
  
  .client-avatar {
    width: 50px;
    height: 50px;
  }
  
  .partners-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>